import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';

export type PlayerListItem = {
  id: string;
  epId: number | null;
  firstName: string;
  lastName: string;
  displayName: string | null;
  preferredName?: string | null;
  classification: 'pro_men' | 'pro_women' | 'prospect';
  status: 'active' | 'archived';
  prospectPriority: number | null;
  dob: string | null;
  birthYear: number | null;
  nhlId: number | null;
  shoots: 'L' | 'R' | null;
  currentTeam: string | null;
  currentLeague: string | null;
  position: string | null;
  jerseyNumber: number | null;
  headshotUrl: string | null;
  heroImageUrl: string | null;
  mediaSourceUrl: string | null;
  mediaCheckedAt: number | null;
  contractExpireYear: number | null;
  hometown: string | null;
  endorsementsText?: string | null;
  cellPhone?: string | null;
  primaryEmail?: string | null;
  recentContractDetails?: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  hasActiveInjury: number;
};

export type PlayersResponse = {
  items: PlayerListItem[];
  total: number;
  hasMore: boolean;
};

export type PlayersFilters = {
  search?: string;
  classification?: string[];
  league?: string[];
  priority?: number[];
  status?: 'active' | 'archived';
  hasInjury?: boolean;
  group?:
    | 'ALL'
    | 'NHL'
    | 'AHL'
    | 'ECHL'
    | 'PWHL'
    | 'EUROPE'
    | 'NCAA'
    | 'JUNIOR'
    | 'USHL'
    | 'WHL'
    | 'CHL'
    | 'NTDP'
    | 'YOUTH'
    | 'PROSPECT';
  sort?: 'priority' | 'last_name' | 'updated' | 'league';
};

function buildQuery(f: PlayersFilters): string {
  const params = new URLSearchParams();
  if (f.search) params.set('search', f.search);
  for (const c of f.classification ?? []) params.append('classification', c);
  for (const l of f.league ?? []) params.append('league', l);
  for (const p of f.priority ?? []) params.append('priority', String(p));
  if (f.status) params.set('status', f.status);
  if (f.hasInjury) params.set('hasInjury', '1');
  if (f.group && f.group !== 'ALL') params.set('group', f.group);
  if (f.sort) params.set('sort', f.sort);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function usePlayers(filters: PlayersFilters) {
  return useQuery<PlayersResponse>({
    queryKey: ['players', filters],
    queryFn: () => apiGet<PlayersResponse>(`/api/players${buildQuery(filters)}`),
  });
}

export type PlayerDetail = PlayerListItem & {
  cheatSheetNotes: string | null;
  brandingSheet: string | null;
  address: string | null;
  endorsementsText: string | null;
  cellPhone: string | null;
  primaryEmail: string | null;
  secondaryEmail: string | null;
  inSeasonMailing: string | null;
  outOfSeasonMailing: string | null;
  spouseName: string | null;
  parentsNames: string | null;
  kid1: string | null;
  kid2: string | null;
  kid3: string | null;
  kid4: string | null;
  financialAdvisor: string | null;
  recentContractDetails: string | null;
  nationality: string | null;
  heightIn: number | null;
  weightLb: number | null;
  draftRights: string | null;
};

export type InjuryRecord = {
  id: string;
  injuryType: string;
  severity: string | null;
  injuryDate: string;
  gameContext: string | null;
  doctor: string | null;
  projectedReturnDate: string | null;
  returnToPlayDate: string | null;
  status: 'active' | 'archived';
  notes: string | null;
  firstGameBackFlagged: number | null;
  countdownAlertSent: number | null;
  createdBy: string | null;
  updatedBy: string | null;
};

export type RecentLine = {
  id: string;
  gameDate: string;
  league: string;
  team: string;
  opponent: string | null;
  isHome: number | null;
  teamScore: number | null;
  opponentScore: number | null;
  result: string | null;
  goals: number | null;
  assists: number | null;
  points: number | null;
  plusMinus: number | null;
  pim: number | null;
  toiSeconds: number | null;
  shots: number | null;
  hits: number | null;
  blocked: number | null;
  giveaways: number | null;
  takeaways: number | null;
  ppToiSeconds: number | null;
  shToiSeconds: number | null;
  decision: string | null;
  goalsAgainst: number | null;
  shotsAgainst: number | null;
  saves: number | null;
  savePct: number | null;
  shutout: number | null;
  source: string;
  sourceGameId: string | null;
  ingestedAt: number;
  isDnp: number;
};

export type PlayerSourceRecord = {
  id: string;
  playerId: string;
  kind: 'identity' | 'profile' | 'media' | 'stats' | 'video';
  provider: string;
  sourceUrl: string;
  observedAt: number;
  detailsJson: string | null;
};

// NHL contract on file at CapWages (weekly sync); kept apart from Scott's notes.
export type NhlContract = {
  source: 'capwages';
  sourceUrl: string;
  contractType: string | null;
  length: string | null;
  totalValue: string | null;
  currentCapHit: string | null;
  expiryStatus: string | null;
  startSeason: string | null;
  endSeason: string | null;
  endYear: number | null;
  signingTeam: string | null;
  signingDate: string | null;
  checkedAt: number;
};

export type PlayerDetailResponse = {
  eliteProspectsConfigured: boolean;
  nhlContract?: NhlContract | null;
  player: PlayerDetail;
  injuries: InjuryRecord[];
  recentLines: RecentLine[];
  events: PlayerEvent[];
  sourceRecords: PlayerSourceRecord[];
  externalProfile: {
    source: 'nhl';
    headshot: string | null;
    heroImage: string | null;
    profileUrl: string;
    landing: {
      currentTeamAbbrev?: string;
      headshot?: string;
      heroImage?: string;
      featuredStats?: {
        season?: number;
        regularSeason?: {
          subSeason?: SeasonStatSummary;
          career?: SeasonStatSummary;
        };
        playoffs?: {
          subSeason?: SeasonStatSummary;
          career?: SeasonStatSummary;
        };
      };
    } | null;
    gameLog: {
      seasonId: number;
      gameTypeId: number;
      gameLog: Array<{
        gameId: number;
        gameDate: string;
        teamAbbrev: string;
        opponentAbbrev: string;
        homeRoadFlag: 'H' | 'R';
        goals?: number;
        assists?: number;
        points?: number;
        plusMinus?: number;
        shots?: number;
        pim?: number;
        toi?: string;
      }>;
    } | null;
    seasonGameLog: {
      seasonId: number;
      gameTypeId: number;
      gameLog: Array<{
        gameId: number;
        gameDate: string;
        teamAbbrev: string;
        opponentAbbrev: string;
        homeRoadFlag: 'H' | 'R';
        goals?: number;
        assists?: number;
        points?: number;
        plusMinus?: number;
        shots?: number;
        shifts?: number;
        pim?: number;
        toi?: string;
      }>;
    } | null;
  } | null;
};

export type PlayerEvent = {
  id: string;
  playerId: string;
  type: 'note' | 'viewing' | 'video' | 'schedule';
  occurredAt: number;
  title: string;
  body: string | null;
  metadataJson: string | null;
  createdAt: number;
  updatedAt: number;
  createdBy: string | null;
  updatedBy: string | null;
};

export type SeasonStatSummary = {
  gamesPlayed?: number;
  goals?: number;
  assists?: number;
  points?: number;
  plusMinus?: number;
  pim?: number;
  shots?: number;
  shootingPctg?: number;
  gameWinningGoals?: number;
  powerPlayGoals?: number;
  powerPlayPoints?: number;
  shorthandedGoals?: number;
  shorthandedPoints?: number;
};

export function usePlayer(playerId: string | undefined) {
  return useQuery<PlayerDetailResponse>({
    queryKey: ['player', playerId],
    enabled: Boolean(playerId),
    queryFn: () => apiGet<PlayerDetailResponse>(`/api/players/${playerId}`),
  });
}
